<template>
  <div class="processing-time-report">
    <!-- Novo layout com filtros na direita e relatório na esquerda -->
    <b-row class="h-100 no-gutters">
      <!-- Coluna da esquerda - Relatório -->
      <b-col cols="9" class="h-100">
        <b-card v-if="isMasterUser" id="report" no-body class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de denúncias · Total: ${totalFilteredCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <b-card-body class="position-relative">
              <div
                class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
              >
                <h2>{{ reportTitle }}</h2>
                <b-badge variant="info" pill class="p-2">
                  <i class="fas fa-file-alt mr-1"></i>
                  Total: {{ totalFilteredCount }}
                </b-badge>
              </div>

              <div
                class="complaints-layout-surface"
                :class="{ 'is-loading': loadingClassifications }"
              >
                <!-- Skeleton Loader -->
                <div v-if="loadingClassifications" class="skeleton-overlay">
                  <div class="table-responsive">
                    <table class="table complaints-layout-table">
                      <thead class="complaints-layout-header">
                        <tr>
                          <th
                            v-for="field in fields"
                            :key="field.key"
                            class="text-center align-middle"
                          >
                            {{ field.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="i in 8" :key="i">
                          <td v-for="field in fields" :key="field.key">
                            <b-skeleton
                              :width="
                                field.key === 'protocol'
                                  ? '120px'
                                  : field.key === 'status'
                                  ? '100px'
                                  : '90%'
                              "
                            ></b-skeleton>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <b-table
                  v-show="!loadingClassifications"
                  :key="tableKey"
                  borderless
                  :responsive="!printing"
                  :items="reportItems"
                  :fields="fields"
                  :filter="currentFilter"
                  :filter-function="filterResults"
                  table-class="complaints-layout-table"
                  thead-class="complaints-layout-header"
                  :tbody-tr-class="getReportRowClass"
                  @filtered="onFiltered"
                >
                  <template #cell(protocol)="data">
                    <router-link
                      v-if="!printing"
                      class="complaints-layout-link"
                      target="_blank"
                      :to="`/denuncias/${data.item._id}`"
                    >
                      {{ data.value }}
                    </router-link>
                    <span v-else>{{ data.value }}</span>
                  </template>
                  <template #cell(status)="data">
                    <b-badge :variant="getStatusBadgeVariant(data.value)">
                      {{ getStatusText(data.value) }}
                    </b-badge>
                  </template>
                  <template #cell(time)="data">
                    {{ duration(data.value) }}
                  </template>
                  <template #cell(committeeAdm)="data">
                    {{
                      getUsersNames(
                        data.item.committee ? data.item.committee.admin : null
                      )
                    }}
                  </template>
                  <template #cell(committee)="data">
                    {{ getUsersNames(data.value ? data.value.users : []) }}
                  </template>
                  <template #cell(companyName)="data">
                    {{ data.value }}
                  </template>
                </b-table>
              </div>
            </b-card-body>
          </div>
        </b-card>

        <b-card v-else id="report" class="h-100">
          <div class="report-print-root" :class="printRootClass">
            <ReportPrintHeader
              v-if="printing"
              :title="printTitle"
              :subtitle="`Relatório de denúncias · Total: ${totalFilteredCount}`"
              :logo="printLogo"
              :generated-at="formatPrintDateTime(printGeneratedAt)"
              :company="printCompany"
              :period="printPeriod"
              :emitter="printEmitter"
            />
            <span v-if="!report.company">Carregando...</span>
            <template v-else>
              <div
                class="d-flex justify-content-between align-items-center mb-3 report-screen-heading"
              >
                <h2>Denúncias da {{ report.company.fantasyName }}</h2>
                <b-badge variant="info" pill class="p-2">
                  <i class="fas fa-file-alt mr-1"></i>
                  Total: {{ totalFilteredCount }}
                </b-badge>
              </div>
              <b-card-body class="position-relative">
                <div
                  class="complaints-layout-surface"
                  :class="{ 'is-loading': loadingClassifications }"
                >
                  <!-- Skeleton Loader -->
                  <div v-if="loadingClassifications" class="skeleton-overlay">
                    <div class="table-responsive">
                      <table class="table complaints-layout-table">
                        <thead class="complaints-layout-header">
                          <tr>
                            <th v-for="field in fields" :key="field.key">
                              {{ field.label }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="i in 8" :key="i">
                            <td v-for="field in fields" :key="field.key">
                              <b-skeleton
                                :width="
                                  field.key === 'protocol'
                                    ? '120px'
                                    : field.key === 'status'
                                    ? '100px'
                                    : '90%'
                                "
                              ></b-skeleton>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <b-table
                    v-show="!loadingClassifications"
                    borderless
                    :responsive="!printing"
                    :filter="currentFilter"
                    :filter-function="filterResults"
                    :items="report.complaints"
                    :fields="fields"
                    class="force-printing-overflow"
                    table-class="complaints-layout-table"
                    thead-class="complaints-layout-header"
                    :tbody-tr-class="getReportRowClass"
                    @filtered="onFiltered"
                  >
                    <template #cell(protocol)="data">
                      <router-link
                        v-if="!printing"
                        class="complaints-layout-link"
                        target="_blank"
                        :to="`/denuncias/${data.item._id}`"
                      >
                        {{ data.value }}
                      </router-link>
                      <span v-else>{{ data.value }}</span>
                    </template>
                    <template #cell(status)="data">
                      <b-badge :variant="getStatusBadgeVariant(data.value)">
                        {{ getStatusText(data.value) }}
                      </b-badge>
                    </template>
                    <template #cell(time)="data">
                      {{ duration(data.value) }}
                    </template>
                    <template #cell(committeeAdm)="data">
                      {{
                        getUsersNames(
                          data.item.committee ? data.item.committee.admin : null
                        )
                      }}
                    </template>
                    <template #cell(committee)="data">
                      {{ getUsersNames(data.value.users) }}
                    </template>
                  </b-table>
                </div>
              </b-card-body>
            </template>
          </div>
        </b-card>
      </b-col>
      <!-- Coluna da direita - Filtros -->
      <b-col cols="3" class="pl-3">
        <b-card header="Parâmetros" class="filters-sidebar h-100">
          <div class="filters-container">
            <!-- Filtro de Empresa (apenas para root ou company-root gestora) -->
            <div v-if="showCompanyFilter" class="filter-group">
              <div class="filter-header" @click="toggleSection('empresa')">
                <h6 class="filter-title">Empresa</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.empresa
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-empresa"
                :visible="!collapsedSections.empresa"
              >
                <b-form-group label="Selecionar Empresa" class="mb-3">
                  <b-form-select
                    v-model="filter.selectedCompany"
                    :options="companyOptions"
                    class="mb-2"
                    @change="onFilterChange"
                  >
                    <template #first>
                      <b-form-select-option :value="null"
                        >Todas as Empresas</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtros básicos -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('periodo')">
                <h6 class="filter-title">Período</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.periodo
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-periodo"
                :visible="!collapsedSections.periodo"
              >
                <b-form-group label="Data inicial" class="mb-2">
                  <b-form-input
                    v-model="filter.fromDate"
                    type="date"
                    @change="onFilterChange"
                  />
                </b-form-group>
                <b-form-group label="Data final" class="mb-3">
                  <b-form-input
                    v-model="filter.toDate"
                    type="date"
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtro de Status -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('situacao')">
                <h6 class="filter-title">Situação</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.situacao
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-situacao"
                :visible="!collapsedSections.situacao"
              >
                <b-form-checkbox-group
                  v-model="filter.status"
                  :options="statusOpts"
                  stacked
                  class="mb-3"
                  @change="onFilterChange"
                />
              </b-collapse>
            </div>

            <!-- Filtros de Classificação -->
            <div v-if="shouldShowDetailedFilters" class="filter-group">
              <div
                class="filter-header"
                @click="toggleSection('classificacoes')"
              >
                <h6 class="filter-title">Classificações</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.classificacoes
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-classificacoes"
                :visible="!collapsedSections.classificacoes"
              >
                <b-form-group
                  v-if="companyClassification.receivingMeans.enabled"
                  label="Meio de Recebimento"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.receivingMeans"
                    :options="getClassificationOpts('receivingMeans')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.origin.enabled"
                  label="Origem"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.origin"
                    :options="getClassificationOpts('origin')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.reportingAgent.enabled"
                  label="Agente Denunciante"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.reportingAgent"
                    :options="getClassificationOpts('reportingAgent')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.level.enabled"
                  label="Classificação/Classe Interna"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.level"
                    :options="getClassificationOpts('level')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.nature.enabled"
                  label="Natureza"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.nature"
                    :options="getClassificationOpts('nature')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.responsibleDepartment.enabled"
                  label="Setor Responsável"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.responsibleDepartment"
                    :options="getClassificationOpts('responsibleDepartment')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.involvedDepartment.enabled"
                  label="Setor da Ocorrência"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.involvedDepartment"
                    :options="getClassificationOpts('involvedDepartment')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.businessUnit.enabled"
                  label="Unidade"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.businessUnit"
                    :options="getClassificationOpts('businessUnit')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.businessGroup.enabled"
                  label="Empresa do Grupo"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.businessGroup"
                    :options="getClassificationOpts('businessGroup')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.relatedProject.enabled"
                  label="Contrato/Projeto"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.relatedProject"
                    :options="getClassificationOpts('relatedProject')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group
                  v-if="companyClassification.complaintClosure.enabled"
                  label="Encerramento"
                  class="mb-2"
                >
                  <b-form-checkbox-group
                    v-model="filter.complaintClosure"
                    :options="getClassificationOpts('complaintClosure')"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Filtros de Comitê -->
            <div v-if="shouldShowDetailedFilters" class="filter-group">
              <div class="filter-header" @click="toggleSection('comite')">
                <h6 class="filter-title">Comitê</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.comite
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-comite"
                :visible="!collapsedSections.comite"
              >
                <b-form-group label="Relator" class="mb-2">
                  <b-form-radio-group
                    v-model="filter.committee.admin"
                    :options="[
                      { value: null, text: 'Todos' },
                      ...companyUsersList,
                    ]"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>

                <b-form-group label="Membros" class="mb-2">
                  <b-form-checkbox-group
                    v-model="filter.committee.members"
                    :options="companyUsersList"
                    stacked
                    @change="onFilterChange"
                  />
                </b-form-group>
              </b-collapse>
            </div>

            <!-- Campos -->
            <div class="filter-group">
              <div class="filter-header" @click="toggleSection('campos')">
                <h6 class="filter-title">Campos</h6>
                <b-button variant="link" size="sm" class="collapse-btn p-0">
                  <i
                    :class="
                      collapsedSections.campos
                        ? 'fas fa-chevron-down'
                        : 'fas fa-chevron-up'
                    "
                  />
                </b-button>
              </div>
              <b-collapse
                id="collapse-campos"
                :visible="!collapsedSections.campos"
              >
                <b-form-checkbox-group
                  v-model="selectedColumns"
                  :options="availableColumns"
                  name="flavour-1"
                  class="mb-3"
                  @change="updateFields"
                />
              </b-collapse>
            </div>

            <!-- Botões de ação -->
            <div class="d-flex flex-column mt-3">
              <b-button
                variant="secondary"
                class="mb-2"
                @click="clearParameters"
              >
                <i class="fas fa-trash"></i> Limpar
              </b-button>
              <b-button variant="primary" @click="print">
                <i class="fas fa-print"></i> Imprimir
              </b-button>
              <b-button variant="success" class="mt-2" @click="exportCSV">
                <i class="fas fa-file-csv"></i> Exportar CSV
              </b-button>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import humanizeDuration from "humanize-duration";
import { mapGetters } from "vuex";
import { processingTimeReport } from "@/api/reports";
import { getStatusText } from "../utils";
import ComplaintStatus from "../../../../../backend/models/ComplaintStatus.ts";
import moment from "moment";
import {
  getCompanyClassifications,
  getCompanyUsers,
  getCompanies,
  getClassificationValues,
} from "@/api/company";
import ClassificationType from "../../../../../backend/models/ClassificationType.ts";
import Papa from "papaparse";
import ReportPrintHeader from "@/components/ReportPrintHeader.vue";
import reportPrintMixin from "@/mixins/reportPrint";

export default {
  name: "ProcessingTimeReport",
  components: {
    ReportPrintHeader,
  },
  mixins: [reportPrintMixin],
  data() {
    const statusOpts = [];
    for (const status of Object.keys(ComplaintStatus)) {
      const text = getStatusText(ComplaintStatus[status]);
      statusOpts.push({ value: ComplaintStatus[status], text });
    }

    return {
      tableKey: 0,
      totalRows: 0,
      loadingClassifications: false,
      statusOpts,
      selectedColumns: [],
      availableColumns: [],
      filter: {
        selectedCompany: null,
        fromDate: null,
        toDate: null,
        status: [],
        receivingMeans: [],
        origin: [],
        reportingAgent: [],
        level: [],
        nature: [],
        responsibleDepartment: [],
        involvedDepartment: [],
        businessUnit: [],
        businessGroup: [],
        relatedProject: [],
        complaintClosure: [],
        committee: {
          admin: null,
          members: [],
        },
      },
      companyClassification: {
        receivingMeans: {},
        origin: {},
        reportingAgent: {},
        level: {},
        nature: {},
        responsibleDepartment: {},
        involvedDepartment: {},
        businessUnit: {},
        businessGroup: {},
        relatedProject: {},
        complaintClosure: {},
      },
      currentFilter: {
        selectedCompany: null,
        status: [],
        toDate: null,
        fromDate: null,
        receivingMeans: [],
        origin: [],
        reportingAgent: [],
        level: [],
        nature: [],
        responsibleDepartment: [],
        involvedDepartment: [],
        businessUnit: [],
        businessGroup: [],
        relatedProject: [],
        complaintClosure: [],
        committee: {
          admin: null,
          members: [],
        },
      },
      report: [],
      hcfg: { language: "pt", largest: 3 },
      fields: [{ key: "protocol", label: "Protocolo", sortable: true }],
      companyUsers: {},
      availableCompanies: [],
      collapsedSections: {
        empresa: false,
        periodo: false,
        situacao: false,
        classificacoes: true,
        comite: true,
        campos: true,
      },
    };
  },
  computed: {
    ...mapGetters([
      "profile",
      "classificationOptions",
      "classificationMaps",
      "portalBucket",
    ]),
    isMasterUser() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    isCompanyManager() {
      return (
        this.profile.role === "company-root" &&
        this.profile.managedCompanies &&
        this.profile.managedCompanies.length > 0
      );
    },
    showCompanyFilter() {
      return this.profile.role === "root" || this.isCompanyManager;
    },
    shouldShowDetailedFilters() {
      // Mostra filtros detalhados se não for master user (comportamento atual)
      // OU se for master user E tiver uma empresa específica selecionada
      if (!this.isMasterUser) {
        return true;
      }
      return this.filter.selectedCompany !== null;
    },
    companyUsersList() {
      const users = [];
      for (const user of Object.values(this.companyUsers)) {
        users.push({ value: user._id, text: `${user.name} ${user.surname}` });
      }
      return users;
    },
    companyOptions() {
      const options = [];

      if (this.availableCompanies && this.availableCompanies.length > 0) {
        this.availableCompanies.forEach((company) => {
          options.push({
            value: company._id,
            text: company.fantasyName || company.name,
          });
        });
      }

      return options;
    },
    filteredReport() {
      if (!this.showCompanyFilter || !this.currentFilter.selectedCompany) {
        return this.report;
      }
      // Verificar se report é um array antes de filtrar
      if (!Array.isArray(this.report)) return this.report;
      return this.report.filter(
        (item) =>
          item.company &&
          item.company._id === this.currentFilter.selectedCompany
      );
    },
    reportItems() {
      if (!this.isMasterUser) {
        return this.report.complaints || [];
      }
      // Master user - report é array plano de complaints
      if (!Array.isArray(this.report)) return [];

      // Filtrar por empresa se uma específica estiver selecionada
      if (this.currentFilter.selectedCompany) {
        return this.report.filter(
          (item) => item.company === this.currentFilter.selectedCompany
        );
      }

      return this.report;
    },
    reportTitle() {
      if (!this.isMasterUser) {
        return "Denúncias";
      }
      if (this.currentFilter.selectedCompany) {
        const selectedCompany = this.companyOptions.find(
          (c) => c.value === this.currentFilter.selectedCompany
        );
        return `Denúncias - ${
          selectedCompany ? selectedCompany.text : "Empresa Selecionada"
        }`;
      }
      return "Denúncias";
    },
    printTitle() {
      if (!this.isMasterUser && this.report && this.report.company) {
        const companyName =
          this.report.company.fantasyName || this.report.company.name;
        return companyName ? `Denúncias da ${companyName}` : this.reportTitle;
      }
      return this.reportTitle;
    },
    totalFilteredCount() {
      if (!this.report) return 0;

      let allComplaints = [];
      if (this.isMasterUser) {
        allComplaints = this.reportItems;
      } else {
        if (this.report.complaints) {
          allComplaints = this.report.complaints;
        }
      }

      return allComplaints.filter((row) =>
        this.filterResults(row, this.currentFilter)
      ).length;
    },
  },

  watch: {
    "filter.selectedCompany": {
      async handler(newValue, oldValue) {
        // Apenas para master users - recarrega dados da empresa selecionada
        if (this.isMasterUser && newValue !== oldValue) {
          await this.loadSelectedCompanyData(newValue);
        }
      },
    },
  },

  async created() {
    // Logo da empresa do usuário que emite o relatório
    await this.loadCompanyPrintLogo(this.profile.company);

    // Carregar empresas para o filtro (root ou company-root gestora)
    if (this.showCompanyFilter) {
      await this.loadCompanies();
    }

    if (this.isMasterUser || this.isCompanyManager) {
      let response;
      try {
        this.updateColumnsOptions();
        // Chamar sem companyId para receber todas as empresas (root ou company-root gestora)
        response = await processingTimeReport(null);
        this.report = response.data;

        // Carregar classificações de todas as empresas do relatório
        await this.loadClassificationsForAllCompanies();

        // Atualizar colunas após classificações carregadas para forçar re-render
        this.updateColumnsOptions();
      } catch (error) {
        if (error.response.status === 403) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
        return;
      }
    } else {
      await this.$store.dispatch(
        "RefreshCompanyClassifications",
        this.profile.company
      );
      let response;
      try {
        response = await getCompanyClassifications(this.profile.company);
        this.companyClassification = response.data;
        this.updateColumnsOptions();
        response = await getCompanyUsers(this.profile.company, true);
        this.companyUsers = response.data.reduce((obj, user) => {
          obj[user["_id"]] = user;
          return obj;
        }, {});
        response = await processingTimeReport(this.profile.company);
      } catch (error) {
        if (error.response.status === 403) {
          this.$bvToast.toast(error.response.data, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        } else {
          this.$bvToast.toast(error.response.statusText, {
            title: "Erro",
            toastClass: "custom-toast custom-toast-error",
            variant: "light",
            solid: true,
            autoHideDelay: 10000,
            noCloseButton: false,
          });
        }
        return;
      }
      this.report = response.data;
      this.totalRows = this.report.complaints.length;
    }
  },
  methods: {
    getStatusText,
    getReportRowClass(item) {
      if (!item) {
        return "";
      }

      const baseClass = "complaints-layout-row";
      switch (item.status) {
        case ComplaintStatus.NEW:
          return `${baseClass} complaints-layout-row--new`;
        case ComplaintStatus.CONCLUDED:
        case ComplaintStatus.DONE:
          return `${baseClass} complaints-layout-row--concluded`;
        default:
          return baseClass;
      }
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }
      return moment(value).format("DD/MM/YYYY HH:mm");
    },
    getStatusClass(status) {
      const statusMap = {
        [ComplaintStatus.NEW]: "status-new",
        [ComplaintStatus.REJECTED]: "status-rejected",
        [ComplaintStatus.IN_PROGRESS]: "status-in-progress",
        [ComplaintStatus.PENDING]: "status-pending",
        [ComplaintStatus.DONE]: "status-done",
        [ComplaintStatus.CONCLUDED]: "status-concluded",
        [ComplaintStatus.REOPENED]: "status-reopened",
        [ComplaintStatus.FORWARDED]: "status-forwarded",
      };
      return statusMap[status] || "status-default";
    },
    getStatusBadgeVariant(status) {
      const statusMap = {
        [ComplaintStatus.NEW]: "primary",
        [ComplaintStatus.REJECTED]: "dark",
        [ComplaintStatus.IN_PROGRESS]: "warning",
        [ComplaintStatus.PENDING]: "info",
        [ComplaintStatus.DONE]: "success",
        [ComplaintStatus.CONCLUDED]: "success",
        [ComplaintStatus.REOPENED]: "warning",
        [ComplaintStatus.FORWARDED]: "primary",
      };
      return statusMap[status] || "secondary";
    },
    duration(value) {
      return humanizeDuration(value, this.hcfg);
    },
    getClassificationOpts(key) {
      return [
        { value: null, text: "Sem Classificação" },
        ...this.classificationOptions[key],
      ];
    },
    async downloadReport(report) {
      const opts = {};
      const toExportData = report.complaints.map((complaint) => {
        return this.fields.reduce((acc, curr) => {
          if (curr.key === "status") {
            acc[curr.label] = getStatusText(complaint[curr.key]);
          } else if (curr.key === "time") {
            acc[curr.label] = this.duration(complaint[curr.key]);
          } else if (curr.key === "committeeAdm") {
            acc[curr.label] = this.getUsersNames(
              complaint.committee ? complaint.committee.admin : null
            );
          } else if (curr.key === "committee") {
            acc[curr.label] = this.getUsersNames(complaint.committee?.users);
          } else if (this.classificationMaps[curr.key]) {
            if (
              complaint.classification &&
              complaint.classification[curr.key]
            ) {
              acc[curr.label] =
                this.classificationMaps[curr.key][
                  complaint.classification[curr.key]
                ] || "Sem Classificação";
            } else {
              acc[curr.label] = "Sem Classificação";
            }
          } else {
            acc[curr.label] = complaint[curr.key];
          }
          return acc;
        }, {});
      });
      let csv = Papa.unparse(
        toExportData,
        Object.assign(
          {
            delimiter: ";",
            encoding: "utf-8",
          },
          opts
        )
      );

      const a = document.createElement("a");
      a.setAttribute("download", "Relatório de Indicadores.csv");
      a.setAttribute(
        "href",
        `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`
      );
      a.setAttribute("target", "_blank");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },

    getUsersNames(ids) {
      if (ids === null || ids === undefined) {
        return "";
      }
      if (ids.length === undefined) {
        ids = [ids];
      }
      let names = "";
      for (let i = 0; i < ids.length; i++) {
        if (i > 0) {
          names += ", ";
        }
        const user = this.companyUsers[ids[i]];
        if (user) {
          names += `${user.name} ${user.surname}`;
        }
      }
      return names;
    },
    clearParameters() {
      this.filter.selectedCompany = null;
      this.filter.fromDate = null;
      this.filter.toDate = null;
      this.filter.status = [];
      this.filter.receivingMeans = [];
      this.filter.origin = [];
      this.filter.reportingAgent = [];
      this.filter.level = [];
      this.filter.nature = [];
      this.filter.responsibleDepartment = [];
      this.filter.involvedDepartment = [];
      this.filter.businessUnit = [];
      this.filter.businessGroup = [];
      this.filter.relatedProject = [];
      this.filter.complaintClosure = [];
      this.filter.committee = {
        admin: null,
        members: [],
      };

      this.receivingMeans = [];
      this.origin = [];
      this.reportingAgent = [];
      this.level = [];
      this.nature = [];
      this.responsibleDepartment = [];
      this.involvedDepartment = [];
      this.businessUnit = [];
      this.businessGroup = [];
      this.relatedProject = [];
      this.complaintClosure = [];

      // Limpar dados da empresa selecionada para master users
      if (this.isMasterUser) {
        this.loadSelectedCompanyData(null);
      } else {
        this.updateColumnsOptions();
      }

      this.currentFilter = {
        selectedCompany: null,
        status: [],
        receivingMeans: [],
        origin: [],
        reportingAgent: [],
        level: [],
        nature: [],
        responsibleDepartment: [],
        involvedDepartment: [],
        businessUnit: [],
        businessGroup: [],
        relatedProject: [],
        complaintClosure: [],
        toDate: null,
        fromDate: null,
        committee: {
          admin: null,
          members: [],
        },
      };
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
    },
    onFilterChange() {
      if (this.filter.fromDate) {
        const dt = new Date(this.filter.fromDate);
        const offset = dt.getTimezoneOffset();
        this.currentFilter.fromDate = moment(dt)
          .add("minute", offset)
          .startOf("day")
          .toDate();
      } else {
        this.currentFilter.fromDate = null;
      }

      if (this.filter.toDate) {
        const dt = new Date(this.filter.toDate);
        const offset = dt.getTimezoneOffset();
        this.currentFilter.toDate = moment(dt)
          .add("minute", offset)
          .endOf("day")
          .toDate();
      } else {
        this.currentFilter.toDate = null;
      }

      this.currentFilter.selectedCompany = this.filter.selectedCompany;
      this.currentFilter.status = this.filter.status;
      this.currentFilter.receivingMeans = this.filter.receivingMeans;
      this.currentFilter.origin = this.filter.origin;
      this.currentFilter.reportingAgent = this.filter.reportingAgent;
      this.currentFilter.level = this.filter.level;
      this.currentFilter.nature = this.filter.nature;
      this.currentFilter.responsibleDepartment =
        this.filter.responsibleDepartment;
      this.currentFilter.involvedDepartment = this.filter.involvedDepartment;
      this.currentFilter.businessUnit = this.filter.businessUnit;
      this.currentFilter.businessGroup = this.filter.businessGroup;
      this.currentFilter.relatedProject = this.filter.relatedProject;
      this.currentFilter.complaintClosure = this.filter.complaintClosure;
      this.currentFilter.committee = this.filter.committee;
    },
    filterResults(row, filter) {
      if (filter.fromDate || filter.toDate) {
        const created = moment(row.createdAt).toDate();
        if (filter.fromDate && filter.fromDate > created) {
          return false;
        }
        if (filter.toDate && filter.toDate < created) {
          return false;
        }
      }
      if (filter.status.length && filter.status.indexOf(row.status) === -1) {
        return false;
      }
      const classifications = [
        "receivingMeans",
        "origin",
        "reportingAgent",
        "level",
        "nature",
        "responsibleDepartment",
        "involvedDepartment",
        "businessUnit",
        "businessGroup",
        "complaintClosure",
        "relatedProject",
      ];
      const rowClassification = row.classification || {};
      for (const key of classifications) {
        if (
          filter[key].length &&
          filter[key].indexOf(rowClassification[key]) === -1
        ) {
          return false;
        }
      }
      if (filter.committee.admin && row.committee) {
        if (row.committee.admin !== filter.committee.admin) {
          return false;
        }
      }
      if (filter.committee.members && row.committee) {
        for (const member of filter.committee.members) {
          if (!row.committee.users.includes(member)) {
            return false;
          }
        }
      }
      return true;
    },

    exportCSV() {
      const classificationKeys = [
        "receivingMeans",
        "origin",
        "reportingAgent",
        "level",
        "nature",
        "responsibleDepartment",
        "involvedDepartment",
        "businessUnit",
        "businessGroup",
        "relatedProject",
        "complaintClosure",
      ];

      const escapeCsv = (value) => {
        if (value === null || value === undefined) return "";
        if (Array.isArray(value)) value = value.join(", ");
        if (typeof value === "object") value = JSON.stringify(value);
        value = String(value);
        const needsQuotes = /[",\n\r;]/.test(value);
        const escaped = value.replace(/"/g, '""');
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const getCellRawValue = (row, key) => {
        if (key in row) return row[key];
        if (classificationKeys.includes(key)) {
          return row.classification ? row.classification[key] : null;
        }
        return null;
      };

      const formatValue = (field, row) => {
        let value = getCellRawValue(row, field.key);
        if (field.formatter) {
          try {
            return field.formatter(value, field.key, row);
          } catch (e) {
            // fallback below
          }
        }
        switch (field.key) {
          case "status":
            return this.getStatusText(row.status);
          case "time":
            return this.duration(row.time);
          case "committeeAdm":
            return this.getUsersNames(
              row.committee ? row.committee.admin : null
            );
          case "committee":
            return this.getUsersNames(row.committee ? row.committee.users : []);
          case "createdAt":
          case "updatedAt":
            return this.formatDate(value);
          default:
            return value == null ? "" : value;
        }
      };

      const headers = this.fields.map((f) => f.label || f.key);
      const headerRow = headers;

      const allRows = [];
      const appendRows = (rows) => {
        for (const row of rows) {
          if (!this.filterResults(row, this.currentFilter)) continue;
          const csvRow = this.fields.map((f) => formatValue(f, row));
          allRows.push(csvRow);
        }
      };

      if (this.isMasterUser) {
        appendRows(this.reportItems);
      } else {
        appendRows(this.report.complaints || []);
      }

      const separator = ";";
      const lines = [headerRow, ...allRows]
        .map((row) => row.map(escapeCsv).join(separator))
        .join("\r\n");

      const bom = "\ufeff"; // Excel-friendly BOM
      const blob = new Blob([bom + lines], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = moment().format("YYYYMMDD_HHmmss");
      a.href = url;
      a.download = `relatorio_tempo_processamento_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    updateFields(checked) {
      const fields = [];
      const classificationFormatter = (value, key, item) => {
        if (!item.classification) {
          return "Sem Classificação";
        }
        value = item.classification[key];
        if (!value) {
          return "Sem Classificação";
        }
        if (!this.classificationMaps[key]) {
          return "Sem Classificação";
        }
        return this.classificationMaps[key][value] || "Sem Classificação";
      };
      for (const col of this.availableColumns) {
        if (checked && checked.indexOf(col.value) === -1) {
          continue;
        }
        const def = {
          key: col.value,
          label: col.text,
          sortable: true,
          thClass: "text-center align-middle",
        };
        if (col.classification) {
          def.formatter = classificationFormatter;
          def.sortByFormatted = true;
          def.sortable = false;
        } else if (col.formatter) {
          def.formatter = col.formatter;
          def.sortByFormatted = true;
        }
        fields.push(def);
      }

      this.fields = [
        {
          key: "protocol",
          label: "Protocolo",
          sortable: true,
          thClass: "text-center align-middle",
        },
        ...fields,
      ];
    },

    updateColumnsOptions() {
      const availableColumns = [
        {
          text: "Data de Recebimento",
          value: "createdAt",
          formatter: this.formatDate,
        },
        {
          text: "Situação",
          value: "status",
        },
        {
          text: "Tempo",
          value: "time",
        },
      ];

      // Adicionar coluna Empresa para Master users
      if (this.isMasterUser) {
        availableColumns.push({
          text: "Empresa",
          value: "companyName",
        });
      }

      if (!this.isMasterUser) {
        availableColumns.push(
          {
            text: "Relator",
            value: "committeeAdm",
          },
          {
            text: "Membros Comitê",
            value: "committee",
          }
        );
      }
      const classification = [
        {
          value: "receivingMeans",
          text: "Meio de Recebimento",
          classification: true,
        },
        { value: "origin", text: "Origem", classification: true },
        {
          value: "reportingAgent",
          text: "Agente Denunciante",
          classification: true,
        },
        {
          value: "level",
          text: "Classificação/Classe Interna",
          classification: true,
        },
        { value: "nature", text: "Natureza", classification: true },
        {
          value: "responsibleDepartment",
          text: "Setor Responsável",
          classification: true,
        },
        {
          value: "involvedDepartment",
          text: "Setor da Ocorrência",
          classification: true,
        },
        { value: "businessUnit", text: "Unidade", classification: true },
        {
          value: "businessGroup",
          text: "Empresa do Grupo",
          classification: true,
        },
        {
          value: "relatedProject",
          text: "Contrato/Projeto",
          classification: true,
        },
        {
          value: "complaintClosure",
          text: "Encerramento",
          classification: true,
        },
      ];

      for (const item of classification) {
        // Para master users visualizando todas as empresas, mostrar todos os campos de classificação
        // Para usuários normais ou empresa específica selecionada, verificar companyClassification
        if (this.isMasterUser && !this.filter.selectedCompany) {
          availableColumns.push(item);
        } else if (this.companyClassification[item.value]) {
          availableColumns.push(item);
        }
      }

      const defaultSelectedColumns = [];
      const fields = [];
      for (const col of availableColumns) {
        defaultSelectedColumns.push(col.value);
        const def = {
          key: col.value,
          label: col.text,
          sortable: true,
          thClass: "text-center align-middle",
        };
        if (col.classification) {
          def.formatter = (value, key, item) => {
            if (!item.classification) {
              return "Sem Classificação";
            }
            value = item.classification[key];
            if (!value) {
              return "Sem Classificação";
            }
            if (!this.classificationMaps[key]) {
              return "Sem Classificação";
            }
            return this.classificationMaps[key][value] || "Sem Classificação";
          };
          def.sortByFormatted = true;
        }
        fields.push(def);
      }

      this.availableColumns = [...availableColumns];
      this.selectedColumns = [...defaultSelectedColumns];

      this.updateFields();
      this.tableKey++;
    },
    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section];
    },

    async loadSelectedCompanyData(companyId) {
      if (!companyId) {
        // Limpar dados quando nenhuma empresa específica está selecionada
        this.companyClassification = {
          receivingMeans: {},
          origin: {},
          reportingAgent: {},
          level: {},
          nature: {},
          responsibleDepartment: {},
          involvedDepartment: {},
          businessUnit: {},
          businessGroup: {},
          relatedProject: {},
          complaintClosure: {},
        };
        this.companyUsers = {};
        this.updateColumnsOptions();
        return;
      }

      this.loadingClassifications = true;

      try {
        // Carregar classificações da empresa selecionada
        await this.$store.dispatch("RefreshCompanyClassifications", companyId);

        const classificationResponse = await getCompanyClassifications(
          companyId
        );
        this.companyClassification = classificationResponse.data;

        // Carregar usuários da empresa para filtros de comitê
        const usersResponse = await getCompanyUsers(companyId, true);
        this.companyUsers = usersResponse.data.reduce((obj, user) => {
          obj[user["_id"]] = user;
          return obj;
        }, {});

        // Atualizar opções de colunas com as classificações carregadas
        this.updateColumnsOptions();

        // Limpar filtros de classificação e comitê para evitar IDs incompatíveis
        this.filter.receivingMeans = [];
        this.filter.origin = [];
        this.filter.reportingAgent = [];
        this.filter.level = [];
        this.filter.nature = [];
        this.filter.responsibleDepartment = [];
        this.filter.involvedDepartment = [];
        this.filter.businessUnit = [];
        this.filter.businessGroup = [];
        this.filter.relatedProject = [];
        this.filter.complaintClosure = [];
        this.filter.committee = {
          admin: null,
          members: [],
        };

        // Sincronizar com currentFilter
        this.onFilterChange();
      } catch (error) {
        console.error("Erro ao carregar dados da empresa selecionada:", error);
        this.$bvToast.toast("Erro ao carregar indicadores da empresa", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
      } finally {
        this.loadingClassifications = false;
      }
    },

    async loadCompanies() {
      if (!this.profile) {
        return;
      }

      try {
        let companies = [];

        if (this.profile.role === "root") {
          // Root: buscar todas as empresas paginadas
          const response = await getCompanies({ size: 1000, page: 1 });
          if (response.data && response.data.docs) {
            companies = response.data.docs;
          } else if (Array.isArray(response.data)) {
            companies = response.data;
          } else if (response.data && response.data.docs) {
            companies = response.data.docs;
          }
        } else if (this.profile.role === "company-root") {
          // Company-root: a API já retorna própria + gerenciadas
          const response = await getCompanies();
          companies = Array.isArray(response.data)
            ? response.data
            : response.data.docs || [];
        }

        this.availableCompanies = companies;
        console.log(
          "ProcessingTimeReport - Companies loaded:",
          this.availableCompanies
        );
      } catch (error) {
        console.error("Erro ao carregar empresas:", error);
      }
    },

    async loadClassificationsForAllCompanies() {
      if (!Array.isArray(this.report) || this.report.length === 0) return;

      this.loadingClassifications = true;

      try {
        // Obter lista única de company IDs do relatório
        const companyIds = [
          ...new Set(this.report.map((item) => item.company)),
        ];

        // Tipos de classificação a serem carregados (mesmos do RefreshCompanyClassifications)
        const classificationTypes = [
          { name: "receivingMeans", type: ClassificationType.RECEIVING_MEANS },
          { name: "origin", type: ClassificationType.ORIGIN },
          { name: "reportingAgent", type: ClassificationType.REPORTING_AGENT },
          { name: "level", type: ClassificationType.LEVEL },
          { name: "nature", type: ClassificationType.NATURE },
          {
            name: "responsibleDepartment",
            type: ClassificationType.RESPONSIBLE_DEPARTMENT,
          },
          {
            name: "involvedDepartment",
            type: ClassificationType.INVOLVED_DEPARTMENT,
          },
          { name: "businessUnit", type: ClassificationType.BUSINESS_UNIT },
          { name: "businessGroup", type: ClassificationType.BUSINESS_GROUP },
          { name: "relatedProject", type: ClassificationType.RELATED_PROJECT },
          {
            name: "complaintClosure",
            type: ClassificationType.COMPLAINT_CLOSURE,
          },
        ];

        // Inicializar mapa de classificações
        const classificationMaps = {};
        for (const ct of classificationTypes) {
          classificationMaps[ct.name] = {};
        }

        // Helper para converter array de itens em mapa {id: value}
        const toMap = (items) => {
          const map = {};
          for (const item of items) {
            map[item._id] = item.value;
          }
          return map;
        };

        // Carregar classificações de cada empresa
        for (const companyId of companyIds) {
          for (const ct of classificationTypes) {
            try {
              const response = await getClassificationValues(
                companyId,
                ct.type
              );
              const companyMap = toMap(response.data);
              // Mesclar no mapa global
              Object.assign(classificationMaps[ct.name], companyMap);
            } catch (error) {
              console.error(
                `Erro ao carregar classificação ${ct.name} da empresa ${companyId}:`,
                error
              );
            }
          }
        }

        // Atualizar o store com todos os mapas mesclados
        this.$store.commit("SET_ALL_CLASSIFICATION_MAPS", classificationMaps);
      } finally {
        this.loadingClassifications = false;
      }
    },
  },
};
</script>
<style scoped>
.processing-time-report {
  height: 100vh;
  overflow: hidden;
}

.processing-time-report .row {
  height: 100%;
  margin: 0;
  align-items: stretch;
}

.processing-time-report .col-3,
.processing-time-report .col-9 {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.processing-time-report .col-9 {
  padding-right: 15px;
}

.processing-time-report .col-3 {
  padding-left: 15px;
}
.filters-sidebar {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f8f9fa;
  border: none;
  border-radius: 8px;
}
/* Harmonize the report card style with the parameters card */
.processing-time-report #report.card {
  border: none;
  border-radius: 8px;
}
.filters-sidebar >>> .card-header {
  background-color: transparent;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #343a40;
  font-size: 1.1rem;
}
.filters-sidebar .card-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.filters-container {
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
}

.processing-time-report .col-9 .card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.processing-time-report .col-9 .report-print-root {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.processing-time-report .col-9 .card-body {
  flex: 1;
  overflow-y: auto;
}

.complaints-layout-surface {
  position: relative;
}

.complaints-layout-surface.is-loading {
  min-height: 360px;
  overflow: hidden;
}

.filter-group {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.filter-group:last-child {
  border-bottom: none;
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.filter-header:hover {
  background-color: transparent;
}

.filter-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.75rem;
  padding-bottom: 0;
  border-bottom: none;
  flex: 1;
  font-size: 0.9rem;
}

.collapse-btn {
  color: #6c757d;
  transition: color 0.2s ease, transform 0.2s ease;
  line-height: 1;
}

.collapse-btn:hover {
  color: #007bff;
  text-decoration: none;
}

.collapse-btn i {
  transition: transform 0.3s ease;
  font-size: 0.875rem;
}

.filter-header:hover .collapse-btn {
  color: #007bff;
}

/* Animação suave para o collapse */
.collapse {
  transition: all 0.3s ease;
}

.collapsing {
  transition: height 0.3s ease;
}

.form-control[multiple] {
  min-height: 80px;
}

/* Estilos para checkboxes e radio buttons */
.form-check {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.form-check-input {
  margin-left: -1.5rem;
}

.form-check-label {
  font-size: 0.875rem;
  line-height: 1.4;
  color: #495057;
  cursor: pointer;
  transition: color 0.2s ease;
}

.form-check-label:hover {
  color: #007bff;
}

.custom-checkbox .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

.custom-radio .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #007bff;
  border-color: #007bff;
}

/* Espaçamento entre grupos de checkboxes */
.filter-group .form-group:not(:last-child) {
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f0f0f0;
}

/* Scroll suave para containers de checkboxes */
.filter-group {
  max-height: none;
}

.filter-group .form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.h-100 {
  min-height: calc(100vh - 120px);
}

/* Customização dos scrollbars */
.filters-container::-webkit-scrollbar {
  width: 6px;
}

.filters-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.filters-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.filters-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.d-flex.flex-column.mt-3 .b-button {
  border-radius: 6px;
  padding: 0.6rem 1rem;
  font-weight: 500;
  transition: background-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
}
.d-flex.flex-column.mt-3 .b-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.d-flex.flex-column.mt-3 .btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}
.d-flex.flex-column.mt-3 .btn-primary {
  background-color: #007bff;
  border-color: #007bff;
}
/* Responsividade */
@media (max-width: 1200px) {
  .processing-time-report {
    height: auto;
    min-height: 100vh;
  }

  .filters-sidebar {
    height: auto;
    min-height: 400px;
  }

  .filters-container {
    height: auto;
    max-height: 400px;
  }

  .processing-time-report .col-3,
  .processing-time-report .col-9 {
    height: auto;
  }
}

@media (max-width: 768px) {
  .processing-time-report .col-3 {
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 20px;
    height: auto;
    padding-left: 0;
  }

  .processing-time-report .col-9 {
    flex: 0 0 100%;
    max-width: 100%;
    height: auto;
    padding-right: 0;
  }

  .processing-time-report .row {
    height: auto;
    flex-direction: column;
  }
}

/* Skeleton Overlay */
.skeleton-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  z-index: 10;
  padding: 0 1rem;
  animation: fadeIn 0.2s ease-in;
  overflow-x: auto;
  overflow-y: auto;
}

.skeleton-overlay table {
  margin-bottom: 0;
}

.skeleton-overlay thead th {
  background-color: #eef1f4;
  border-bottom: none;
  font-weight: 600;
  color: #495057;
  padding: 0.75rem;
  white-space: nowrap;
}

.skeleton-overlay thead th:first-child {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.skeleton-overlay thead th:last-child {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.skeleton-overlay tbody td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: none;
  border-bottom: 1px solid #e6eaef;
}

.skeleton-overlay tbody tr:hover {
  background-color: transparent !important;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
<style>
.report-table {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}

.report-table thead th,
.report-table-header th {
  background-color: #eef1f4;
  border: none;
  color: #56606b;
  font-weight: 600;
  padding: 1rem;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}

.report-table thead th:first-child,
.report-table-header th:first-child {
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
}

.report-table thead th:last-child,
.report-table-header th:last-child {
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
}

.report-table tbody tr,
.report-table-row {
  background-color: #fff;
}

.report-table tbody tr:hover,
.report-table-row:hover {
  background-color: #fbfcfe;
}

.report-table tbody td,
.report-table-row td {
  border-top: none;
  border-bottom: 1px solid #e6eaef;
  padding: 1rem;
  vertical-align: middle;
}

.report-table tbody tr:hover td,
.report-table-row:hover td {
  background-color: #fbfcfe;
}

.protocol-link {
  color: #0b74de;
  font-weight: 500;
  text-decoration: none;
}

.protocol-link:hover {
  color: #0759aa;
  text-decoration: underline;
}

.status-badge {
  padding: 0.4em 0.8em;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
}
.status-new {
  background-color: #fce4e4;
  color: #c0392b;
}
.status-in-progress {
  background-color: #e0e7ff;
  color: #3730a3;
}
.status-done {
  background-color: #d1fae5;
  color: #065f46;
}
.status-concluded {
  background-color: #d1fae5;
  color: #065f46;
}
.status-rejected {
  background-color: #e5e7eb;
  color: #4b5563;
}
.status-pending,
.status-forwarded,
.status-reopened,
.status-default {
  background-color: #e0e7ff;
  color: #3730a3;
}
</style>
